import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const CONFIG = {
  canvasWidth: 1200,
  canvasHeight: 600,
  barWidth: 14,
  barSpacing: 4,
  letterGap: 40,
  separatorBars: 2,
  separatorMargin: 0,
  separatorHeight: 0,
  separatorAudioMin: 120,
  separatorAudioMax: 220,
  baseHeight: 40,
  maxHeight: 250,
  animationPhases: {
    static: 1000,
    audioEffect: 800,
    morphing: 600,
    finalStatic: 600
  },
  restartDelay: 1200
};

const randomInRange = (min, max) => min + Math.random() * (max - min);

class Segment {
  constructor(yOffset, height) {
    this.baseYOffset = yOffset;
    this.baseHeight = height;
    this.currentYOffset = yOffset;
    this.currentHeight = height;
  }

  updateAudio(variation) {
    this.currentHeight = Math.max(10, this.baseHeight + variation);
  }

  morphTo(newYOffset, newHeight, progress) {
    this.currentYOffset = this.baseYOffset + (newYOffset - this.baseYOffset) * progress;
    this.currentHeight = this.baseHeight + (newHeight - this.baseHeight) * progress;
  }
}

class Column {
  constructor(x, segments, group, options = {}) {
    this.x = x;
    this.segments = segments.map(s => new Segment(s.yOffset, s.height));
    this.baseSegments = segments;
    this.targetSegments = null;
    this.targetX = x;
    this.group = group;
    this.isFiller = options.isFiller || false;
    this.audioTargetHeight = options.audioTargetHeight;
    this.targetOpacity = options.targetOpacity ?? 1;
    this.audioOffset = Math.random() * Math.PI * 2;
    this.audioFrequency = 3 + Math.random() * 5;
    this.opacity = 1;
    this.morphStartSegments = null;
    this.morphStartX = x;
  }

  updateAudio(time, intensity) {
    const wave = Math.sin(time * this.audioFrequency * 3 + this.audioOffset);
    const variation = wave * intensity * 80;

    if (this.group === 'separator') {
      this.segments.forEach(segment => {
        const targetH = this.audioTargetHeight ?? segment.baseHeight;
        const ramp = targetH * Math.max(intensity, 0);
        const wiggle = variation * intensity;
        segment.currentHeight = Math.max(0, ramp + wiggle);
      });
    } else {
      this.segments.forEach(segment => {
        segment.updateAudio(variation);
      });
    }
  }

  startMorph() {
    this.morphStartSegments = this.segments.map(seg => ({
      yOffset: seg.currentYOffset,
      height: seg.currentHeight
    }));
    this.morphStartX = this.x;
  }

  morphTo(targetSegments, progress) {
    const startSegments = this.morphStartSegments || this.segments.map(seg => ({
      yOffset: seg.baseYOffset,
      height: seg.baseHeight
    }));
    const maxSegments = Math.max(startSegments.length, targetSegments.length);

    while (this.segments.length < maxSegments) {
      this.segments.push(new Segment(0, 0));
    }

    for (let i = 0; i < maxSegments; i++) {
      const start = startSegments[i] || { yOffset: 0, height: 0 };
      const target = targetSegments[i] || { yOffset: start.yOffset, height: 0 };
      this.segments[i].currentYOffset = start.yOffset + (target.yOffset - start.yOffset) * progress;
      this.segments[i].currentHeight = start.height + (target.height - start.height) * progress;
    }

    this.x = this.morphStartX + (this.targetX - this.morphStartX) * progress;
    const finalOpacity = this.isFiller ? 0 : this.targetOpacity;
    this.opacity = progress >= 1 ? finalOpacity : 1;
  }

  applyWiggle(time, strength) {
    if (strength <= 0) return;
    const wave = Math.sin(time * this.audioFrequency * 3 + this.audioOffset);
    const jitter = (Math.random() - 0.5) * 30;
    const wiggle = wave * strength * 90 + jitter * strength;
    this.segments.forEach(seg => {
      seg.currentHeight = Math.max(0, seg.currentHeight + wiggle);
    });
  }

  fadeOut(progress) {
    this.opacity = 1 - progress;
  }

  draw(ctx, fillStyle) {
    if (this.opacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = this.opacity;
    ctx.fillStyle = fillStyle;
    this.segments.forEach(segment => {
      if (segment.currentHeight > 0) {
        const centerY = CONFIG.canvasHeight / 2;
        const y = centerY + segment.currentYOffset - segment.currentHeight / 2;
        ctx.fillRect(this.x, y, CONFIG.barWidth, segment.currentHeight);
      }
    });
    ctx.restore();
  }
}

const ENIGMA_PATTERN = {
  E: [
    { segments: [{ yOffset: 0, height: 220 }] },
    { segments: [{ yOffset: 16, height: 252 }] },
    { segments: [{ yOffset: 3, height: 252 }] },
    {
      segments: [
        { yOffset: -91, height: 40 },
        { yOffset: -17, height: 46 },
        { yOffset: 99, height: 79 }
      ]
    },
    {
      segments: [
        { yOffset: -91, height: 40 },
        { yOffset: -17, height: 46 },
        { yOffset: 86, height: 53 }
      ]
    },
    {
      segments: [
        { yOffset: -91, height: 40 },
        { yOffset: -17, height: 46 },
        { yOffset: 92, height: 66 }
      ]
    }
  ],
  N: [
    { segments: [{ yOffset: 21, height: 263 }] },
    { segments: [{ yOffset: 9, height: 238 }] },
    { segments: [{ yOffset: -38, height: 80 }] },
    { segments: [{ yOffset: -3, height: 81 }] },
    { segments: [{ yOffset: -21, height: 247 }] },
    { segments: [{ yOffset: 24, height: 270 }] },
    { segments: [{ yOffset: 7, height: 235 }] }
  ],
  I: [
    { segments: [{ yOffset: 0, height: 223 }] },
    { segments: [{ yOffset: 22, height: 265 }] },
    { segments: [{ yOffset: -3, height: 286 }] },
    { segments: [{ yOffset: 0, height: 195 }] }
  ],
  G: [
    { segments: [{ yOffset: 0, height: 151 }] },
    { segments: [{ yOffset: -8, height: 210 }] },
    { segments: [{ yOffset: 5, height: 236 }] },
    {
      segments: [
        { yOffset: -92, height: 43 },
        { yOffset: 115, height: 86 }
      ]
    },
    {
      segments: [
        { yOffset: -92, height: 43 },
        { yOffset: 24, height: 46 },
        { yOffset: 127, height: 111 }
      ]
    },
    {
      segments: [
        { yOffset: -75, height: 78 },
        { yOffset: 55, height: 109 }
      ]
    },
    {
      segments: [
        { yOffset: -66, height: 60 },
        { yOffset: 77, height: 152 }
      ]
    }
  ],
  M: [
    { segments: [{ yOffset: 3, height: 239 }] },
    { segments: [{ yOffset: 14, height: 260 }] },
    { segments: [{ yOffset: 42, height: 256 }] },
    { segments: [{ yOffset: 18, height: 110 }] },
    { segments: [{ yOffset: 56, height: 104 }] },
    { segments: [{ yOffset: 12, height: 100 }] },
    { segments: [{ yOffset: 53, height: 277 }] },
    { segments: [{ yOffset: 19, height: 271 }] },
    { segments: [{ yOffset: 12, height: 257 }] }
  ],
  A: [
    { segments: [{ yOffset: 85, height: 212 }] },
    { segments: [{ yOffset: 39, height: 231 }] },
    { segments: [{ yOffset: -17, height: 202 }] },
    {
      segments: [
        { yOffset: -94, height: 112 },
        { yOffset: 50, height: 46 }
      ]
    },
    { segments: [{ yOffset: -20, height: 197 }] },
    { segments: [{ yOffset: 4, height: 246 }] },
    { segments: [{ yOffset: 69, height: 185 }] }
  ]
};

const MEGIA_PATTERN = {
  M: ENIGMA_PATTERN.M,
  E: ENIGMA_PATTERN.E,
  G: ENIGMA_PATTERN.G,
  space: [{ segments: [] }],
  I: ENIGMA_PATTERN.I,
  A: ENIGMA_PATTERN.A
};

const computePatternWidth = (pattern, options = {}) => {
  const { letterSpacing = CONFIG.letterGap, withSeparators = false } = options;
  const letters = Object.values(pattern);
  const gaps = letters.length - 1;
  const totalColumns = letters.reduce((sum, letter) => sum + letter.length, 0);
  const gapWidth = withSeparators
    ? CONFIG.separatorMargin * 2 + CONFIG.separatorBars * (CONFIG.barWidth + CONFIG.barSpacing)
    : letterSpacing;
  return totalColumns * (CONFIG.barWidth + CONFIG.barSpacing) + gaps * gapWidth;
};

const ENIGMA_PATTERN_WIDTH = computePatternWidth(ENIGMA_PATTERN, { withSeparators: true });
const MEGIA_PATTERN_WIDTH = computePatternWidth(MEGIA_PATTERN, { withSeparators: true });
const MAX_PATTERN_WIDTH = Math.max(ENIGMA_PATTERN_WIDTH, MEGIA_PATTERN_WIDTH);
CONFIG.canvasWidth = MAX_PATTERN_WIDTH + 32;
const REFERENCE_PATTERN = MAX_PATTERN_WIDTH === ENIGMA_PATTERN_WIDTH ? ENIGMA_PATTERN : MEGIA_PATTERN;
const REFERENCE_TOTAL_WIDTH = ENIGMA_PATTERN_WIDTH;
const REFERENCE_LETTER_COUNT = Object.keys(ENIGMA_PATTERN).length;

const generateColumns = (pattern, options = {}) => {
  const { letterSpacing = CONFIG.letterGap, fillGaps = false, withSeparators = false } = options;

  const columns = [];
  const letters = Object.values(pattern);
  const gaps = letters.length - 1;
  const totalColumns = letters.reduce((sum, letter) => sum + letter.length, 0);
  const gapWidth = withSeparators
    ? CONFIG.separatorMargin * 2 + CONFIG.separatorBars * (CONFIG.barWidth + CONFIG.barSpacing)
    : letterSpacing;
  const totalWidth = totalColumns * (CONFIG.barWidth + CONFIG.barSpacing) + gaps * gapWidth;

  let currentX = (CONFIG.canvasWidth - totalWidth) / 2;

  const entries = Object.entries(pattern);
  entries.forEach(([letterName, columnDefs], idx) => {
    for (const columnDef of columnDefs) {
      if (fillGaps && columnDef.segments.length === 0) {
        const randomHeight = 150 + Math.random() * 100;
        columns.push(new Column(currentX, [{ yOffset: 0, height: randomHeight }], letterName));
      } else if (columnDef.segments.length > 0) {
        columns.push(new Column(currentX, columnDef.segments, letterName));
      }
      currentX += CONFIG.barWidth + CONFIG.barSpacing;
    }
    const isLast = idx === entries.length - 1;
    if (!isLast) {
      if (withSeparators) {
        currentX += CONFIG.separatorMargin;
        for (let s = 0; s < CONFIG.separatorBars; s++) {
          const sep = new Column(currentX, [{ yOffset: 0, height: CONFIG.separatorHeight }], 'separator');
          columns.push(sep);
          currentX += CONFIG.barWidth + CONFIG.barSpacing;
        }
        currentX += CONFIG.separatorMargin;
      } else {
        currentX += letterSpacing;
      }
    }
  });

  return columns;
};

const buildAlignedColumns = (startPattern, targetPattern) => {
  const referenceColumns = generateColumns(REFERENCE_PATTERN, { withSeparators: true });
  const startColumns = generateColumns(startPattern, { withSeparators: true });
  const targetColumns = generateColumns(targetPattern, { withSeparators: true });

  const refLen = referenceColumns.length;
  const startLen = startColumns.length;
  const targetLen = targetColumns.length;

  return referenceColumns.map((refCol, idx) => {
    const t = refLen > 1 ? idx / (refLen - 1) : 0;
    const startIdx = Math.round(t * Math.max(0, startLen - 1));
    const targetIdx = Math.round(t * Math.max(0, targetLen - 1));

    const startCol = startColumns[startIdx] || { baseSegments: [], group: 'none', x: refCol.x };
    const targetCol = targetColumns[targetIdx] || { baseSegments: [], group: 'none', x: refCol.x };

    const group = refCol.group === 'separator' ? 'separator' : startCol.group || refCol.group;
    const startSegments = group === 'separator'
      ? [{ yOffset: 0, height: 1 }]
      : startCol.baseSegments;

    const col = new Column(refCol.x, startSegments, group, {
      targetOpacity: targetCol.group === 'separator' ? 0 : 1,
      audioTargetHeight: group === 'separator'
        ? randomInRange(CONFIG.separatorAudioMin, CONFIG.separatorAudioMax)
        : undefined
    });

    col.targetSegments = targetCol.baseSegments.length ? targetCol.baseSegments : [];
    col.targetX = refCol.x;
    return col;
  });
};

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const AnimatedLogo = React.forwardRef(({
  compact = false,
  fullWidth = false,
  animate = true,
  staticLetter = null,
  bare = false,
  heightOverride = null
}, forwardedRef) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const directionRef = useRef(true); // true: ENIGMA -> MEGIA, false: MEGIA -> ENIGMA
  const snapshotRef = useRef(null);
  const remainingCyclesRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    canvas.width = CONFIG.canvasWidth;
    canvas.height = CONFIG.canvasHeight;

    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, CONFIG.canvasWidth, CONFIG.canvasHeight);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');

    let animationState = {
      columns: [],
      startTime: 0,
      isAnimating: false,
      currentPhase: 'static',
      startPattern: ENIGMA_PATTERN,
      targetPattern: MEGIA_PATTERN
    };

    const getPatternId = (pattern) => (pattern === MEGIA_PATTERN ? 'MEGIA' : 'ENIGMA');

    const applySnapshotToColumns = (columns, patternId) => {
      if (!snapshotRef.current || snapshotRef.current.pattern !== patternId) return;
      const snapCols = snapshotRef.current.columns;
      if (!snapCols || !snapCols.length) return;
      columns.forEach((col, idx) => {
        const snapCol = snapCols[idx];
        if (!snapCol) return;
        const maxLen = Math.max(col.segments.length, snapCol.segments.length);
        const nextSegments = [];
        for (let i = 0; i < maxLen; i++) {
          const baseSource = snapCol.segments[i] || col.segments[i] || col.segments[col.segments.length - 1] || { baseYOffset: 0, baseHeight: 0 };
          const snapSeg = snapCol.segments[i];
          const nextSeg = new Segment(baseSource.baseYOffset ?? 0, baseSource.baseHeight ?? 0);
          if (snapSeg) {
            nextSeg.currentYOffset = snapSeg.yOffset;
            nextSeg.currentHeight = snapSeg.height;
          }
          nextSegments.push(nextSeg);
        }
        col.segments = nextSegments;
      });
    };

    const storeSnapshot = (pattern) => {
      snapshotRef.current = {
        pattern: getPatternId(pattern),
        columns: animationState.columns.map(col => ({
          segments: col.segments.map(seg => ({
            yOffset: seg.currentYOffset,
            height: seg.currentHeight,
            baseYOffset: seg.baseYOffset,
            baseHeight: seg.baseHeight
          }))
        }))
      };
    };

    const clear = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const renderColumns = (columnsToRender = animationState.columns, options = {}) => {
      const { disableScale = false, targetWidth = null, tightenY = false } = options;
      clear();
      if (!columnsToRender.length) return;

      const minX = Math.min(...columnsToRender.map(col => col.x));
      const maxX = Math.max(...columnsToRender.map(col => col.x)) + CONFIG.barWidth;
      const contentWidth = Math.max(1, maxX - minX);
      let scaleX;
      if (targetWidth) {
        scaleX = targetWidth / contentWidth;
      } else if (disableScale) {
        scaleX = 1;
      } else {
        scaleX = canvas.width / contentWidth;
      }
      const virtualWidth = canvas.width / scaleX;
      const offsetX = (virtualWidth - contentWidth) / 2 - minX;

      ctx.save();
      let scaleY = 1;
      let offsetY = 0;

      if (tightenY) {
        let minY = Number.POSITIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        columnsToRender.forEach(column => {
          column.segments.forEach(segment => {
            if (segment.currentHeight <= 0) return;
            const centerY = CONFIG.canvasHeight / 2;
            const y = centerY + segment.currentYOffset - segment.currentHeight / 2;
            const y2 = y + segment.currentHeight;
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y2);
          });
        });
        if (minY === Number.POSITIVE_INFINITY || maxY === Number.NEGATIVE_INFINITY) {
          minY = 0;
          maxY = CONFIG.canvasHeight;
        }
        const contentHeight = Math.max(1, maxY - minY);
        const maxScaleY = 1.2;
        scaleY = Math.min(canvas.height / contentHeight, maxScaleY);
        const virtualHeight = canvas.height / scaleY;
        offsetY = (virtualHeight - contentHeight) / 2 - minY;
      }

      ctx.scale(scaleX, scaleY);
      ctx.translate(offsetX, offsetY);
      columnsToRender.forEach(column => column.draw(ctx, gradient));
      ctx.restore();
    };

    if (!animate) {
      const pattern = staticLetter && ENIGMA_PATTERN[staticLetter]
        ? { [staticLetter]: ENIGMA_PATTERN[staticLetter] }
        : ENIGMA_PATTERN;
      const staticColumns = generateColumns(pattern, { withSeparators: true });
      if (staticLetter) {
        const averageLetterWidth = REFERENCE_TOTAL_WIDTH / REFERENCE_LETTER_COUNT;
        renderColumns(staticColumns, { targetWidth: averageLetterWidth, tightenY: true });
      } else {
        renderColumns(staticColumns, { disableScale: true });
      }
      return undefined;
    }

    const stepAnimation = (timestamp) => {
      if (!animationState.isAnimating) return;

      const elapsed = timestamp - animationState.startTime;
      const phases = CONFIG.animationPhases;

      if (elapsed < phases.static) {
        animationState.currentPhase = 'static';
        renderColumns();
      } else if (elapsed < phases.static + phases.audioEffect) {
        if (animationState.currentPhase !== 'audio') {
          animationState.currentPhase = 'audio';
          animationState.columns = buildAlignedColumns(animationState.startPattern, animationState.targetPattern);
        }

        const audioTime = (elapsed - phases.static) / 1000;
        const intensity = Math.min(1, (elapsed - phases.static) / 300);

        animationState.columns.forEach(column => {
          column.updateAudio(audioTime, intensity);
        });

        renderColumns();
      } else if (elapsed < phases.static + phases.audioEffect + phases.morphing) {
        if (animationState.currentPhase !== 'morphing') {
          animationState.currentPhase = 'morphing';
          animationState.columns.forEach(column => column.startMorph());
        }

        const morphStart = phases.static + phases.audioEffect;
        const morphProgress = (elapsed - morphStart) / phases.morphing;
        const easedProgress = easeInOutCubic(morphProgress);
        const wiggleStrength = Math.max(0, 1 - easedProgress) * 1.2;

        animationState.columns.forEach(column => {
          if (column.targetSegments) {
            column.morphTo(column.targetSegments, easedProgress);
          }
          column.applyWiggle((elapsed - phases.static) / 1000, wiggleStrength * 0.6);
        });

        renderColumns();
      } else if (elapsed < phases.static + phases.audioEffect + phases.morphing + phases.finalStatic) {
        if (animationState.currentPhase !== 'finalStatic') {
          animationState.currentPhase = 'finalStatic';
          animationState.columns.forEach(column => {
            if (column.targetSegments) {
              column.morphTo(column.targetSegments, 1);
            }
          });
          storeSnapshot(animationState.targetPattern);
        }
        renderColumns();
      } else {
        animationState.isAnimating = false;
        animationState.currentPhase = 'complete';
        remainingCyclesRef.current -= 1;
        if (remainingCyclesRef.current > 0) {
          startAnimation();
        }
        return;
      }

      animationFrameRef.current = requestAnimationFrame(stepAnimation);
    };

    const startAnimation = () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      const forward = directionRef.current;
      animationState.startPattern = forward ? ENIGMA_PATTERN : MEGIA_PATTERN;
      animationState.targetPattern = forward ? MEGIA_PATTERN : ENIGMA_PATTERN;
      animationState.columns = buildAlignedColumns(animationState.startPattern, animationState.targetPattern);
      applySnapshotToColumns(animationState.columns, getPatternId(animationState.startPattern));
      animationState.startTime = performance.now();
      animationState.isAnimating = true;
      animationState.currentPhase = 'static';
      directionRef.current = !directionRef.current;
      animationFrameRef.current = requestAnimationFrame(stepAnimation);
    };

    remainingCyclesRef.current = 2; // forward + reverse
    directionRef.current = true;
    startAnimation();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
    };
  }, [animate, staticLetter]);

  const baseHeight = heightOverride || 72;
  const baseWidth = Math.round((CONFIG.canvasWidth / CONFIG.canvasHeight) * baseHeight);

  if (bare) {
    return (
      <canvas
        ref={(node) => {
          if (typeof forwardedRef === 'function') {
            forwardedRef(node);
          } else if (forwardedRef) {
            forwardedRef.current = node;
          }
          canvasRef.current = node;
        }}
        style={{
          width: baseWidth,
          height: baseHeight,
          display: 'block'
        }}
      />
    );
  }

  return (
    <Box
      ref={forwardedRef}
      sx={{
        width: fullWidth ? '100%' : baseWidth,
        height: fullWidth ? 88 : baseHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: fullWidth ? 0 : 0.75,
        borderRadius: fullWidth ? 0 : 2,
        background: 'transparent',
        boxShadow: fullWidth ? 'none' : '0 10px 26px rgba(102, 126, 234, 0.18)',
        overflow: 'hidden'
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
});

AnimatedLogo.displayName = 'AnimatedLogo';

export default AnimatedLogo;
